import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import {
  removeSearchHistoryItem,
  startLeadFinderSearch,
} from "@app/component/leadFinder/store/actions";
import {
  getLeadFinderRecentSearches,
  getLeadFinderSavedSearches,
} from "@app/component/leadFinder/store/selectors";
import { LEAD_FINDER_MAX_SEARCHES } from "@app/component/leadFinder/utils/consts";
import { isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import MapViewportState from "@app/types/map/MapViewportState";
import showLeadFinderLimitAlert from "@app/util/leadFinder/showLeadFinderLimitAlert";

import styles from "./History.module.scss";

interface Props {
  isOwner: boolean;
  onBeforeSearch?: () => void;
  onSearchSuccess?: (bounds?: google.maps.LatLngBounds) => void;
  recentSearches: Array<string>;
  removeSearchHistoryItem: typeof removeSearchHistoryItem;
  savedSearches: Array<string>;
  startLeadFinderSearch: typeof startLeadFinderSearch.request;
  viewport?: MapViewportState;
}

const messages = defineMessages({
  empty: {
    id: "leadFinder.searchPopover.history.empty",
    defaultMessage: "No search history",
    description: "Lead Finder search box no search history title",
  },
  remove: {
    id: "leadFinder.searchPopover.history.remove",
    defaultMessage: "Remove",
    description: "Lead Finder popover search item - remove button title",
  },
  title: {
    id: "leadFinder.searchPopover.history.title",
    defaultMessage: "Quick Search",
    description: "Lead Finder popover quick search title",
  },
});

const History = ({
  isOwner,
  onBeforeSearch,
  onSearchSuccess,
  recentSearches,
  removeSearchHistoryItem,
  savedSearches,
  startLeadFinderSearch,
  viewport,
}: Props) => {
  const intl = useIntl();

  const hasHistory = recentSearches.length + savedSearches.length > 0;
  const recentLimit = Math.max(LEAD_FINDER_MAX_SEARCHES - savedSearches.length, 0);

  const recent = useMemo(() => recentSearches.slice(0, recentLimit), [recentLimit, recentSearches]);
  const saved = useMemo(() => savedSearches.slice(0, LEAD_FINDER_MAX_SEARCHES), [savedSearches]);

  const onSearch = useCallback(
    (value: string) => {
      startLeadFinderSearch({ onBeforeSearch, onSearchSuccess, search: value, viewport });
    },
    [onBeforeSearch, onSearchSuccess, startLeadFinderSearch, viewport]
  );

  const handleSearch = useCallback(
    (value: string) => {
      startLeadFinderSearch({
        onBeforeSearch,
        onLimitThresholdReached: (limits) => {
          showLeadFinderLimitAlert(intl, limits, isOwner, onSearch, value);
        },
        onSearchSuccess,
        search: value,
        viewport,
      });
    },
    [intl, isOwner, onBeforeSearch, onSearch, onSearchSuccess, startLeadFinderSearch, viewport]
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {hasHistory ? intl.formatMessage(messages.title) : intl.formatMessage(messages.empty)}
      </div>

      {hasHistory && (
        <div className={styles.list}>
          {saved.map((query, index) => (
            <div className={styles.item} key={index}>
              <div className={styles.content}>
                <Button
                  block
                  className={styles.btn}
                  icon={<FontAwesomeIcon icon={faStar} />}
                  onClick={() => handleSearch(query)}
                  size="small"
                  type="text"
                >
                  {query}
                </Button>

                <Tooltip placement="right" title={intl.formatMessage(messages.remove)}>
                  <Button
                    className={styles.removeBtn}
                    icon={<FontAwesomeIcon icon={faMinusCircle} />}
                    iconOnly
                    onClick={() => removeSearchHistoryItem({ search: query })}
                    size="small"
                    type="link"
                  />
                </Tooltip>
              </div>
            </div>
          ))}

          {recent.map((query, index) => (
            <div className={styles.item} key={index}>
              <div className={styles.content}>
                <Button
                  block
                  className={styles.btn}
                  icon={<FontAwesomeIcon icon={faClock} />}
                  onClick={() => handleSearch(query)}
                  size="small"
                  type="text"
                >
                  {query}
                </Button>

                <Tooltip placement="right" title={intl.formatMessage(messages.remove)}>
                  <Button
                    className={styles.removeBtn}
                    icon={<FontAwesomeIcon icon={faMinusCircle} />}
                    iconOnly
                    onClick={() => removeSearchHistoryItem({ search: query })}
                    size="small"
                    type="link"
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  recentSearches: getLeadFinderRecentSearches(state),
  savedSearches: getLeadFinderSavedSearches(state),
});

const mapDispatchToProps = {
  removeSearchHistoryItem: removeSearchHistoryItem,
  startLeadFinderSearch: startLeadFinderSearch.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(History);
